import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiAuthError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Wizard, DataTable, StatCard, useToast } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import { useIsCompact } from '../lib/useMediaQuery';
import { humanizeToken } from '../lib/inlineAttribution';
import { readsSentence, unrecognizedMessage } from '../lib/extractionCopy';

/**
 * ImportWizard — /imports
 *
 * Turn outside data into platform data through a Source → Map → Review → Commit flow.
 *
 * TWO SOURCES, ONE PIPELINE. The spreadsheet path (CSV/TSV upload → column mapping →
 * auto-created custom fields) is the default and is unchanged. Alongside it, AI can read
 * an uploaded document (a notice of value, a tax statement) and propose its figures.
 * Both converge on the SAME review step and the SAME commit — reading a document is an
 * additional option, not a replacement, so if it is switched off or unconfigured nothing
 * about importing breaks. The AI source renders visible-but-disabled with the reason in
 * that case, and the spreadsheet source stays selected.
 *
 * Wired to the shipped staged-import pipeline (src/routes/import.js, frozen
 * column_mapping_json v1 per services/spreadsheet-parser.js):
 *   POST /api/imports/preview           — parse + column profile (no writes)
 *   POST /api/imports/stage             — stage rows for review
 *   GET  /api/imports/:batchId          — batch status (review step)
 *   DELETE /api/imports/:batchId        — rollback a staged batch
 *   POST /api/imports/:batchId/commit   — commit into real tables
 *
 * …plus the document-reading source (src/routes/document-extract.js), which stages as
 * part of the read and therefore skips the Map step:
 *   GET  /api/document-extract/availability         — is the option offerable?
 *   POST /api/document-extract/document/:documentId  — read → staged batch
 *
 * The parser is text-only (CSV/TSV); binary .xlsx is not yet supported and is
 * rejected client-side with a clear message.
 *
 * Domain 19.3 — Per-Client Custom Fields & Configurability. Gated on
 * import_batch.can_add.
 */

interface ParsedColumn {
  name: string;
  sample: string;
  detected_type: 'text' | 'number' | 'date' | 'boolean';
}

type MappingTarget = 'skip' | 'new_custom_field';
interface ColumnMapping {
  target: MappingTarget;
  label: string;
  dataType: ParsedColumn['detected_type'];
}

interface ClientRow { id: string; name: string; }

/** Server shapes — src/services/import.js + spreadsheet-parser.js (frozen v1). */
interface PreviewColumn {
  source_column: string;
  inferred_type: string;
  non_empty_count: number;
  sample_values: unknown[];
}
interface MappedPreviewRow {
  record: Record<string, unknown>;
  customFields: Array<{ field_key: string; label: string; value: unknown }>;
  matchKey: string | null;
  error: string | null;
}
interface PreviewResponse {
  targetEntityType: string;
  headers: string[];
  columns: PreviewColumn[];
  rowCount: number;
  rows: MappedPreviewRow[];
  parserVersion: string;
}
interface StageResponse {
  batchId: string;
  table: string;
  mode: string;
  totalRecords: number;
  validCount: number;
  errorCount: number;
  matchCounts?: { matched?: number; ambiguous?: number; new_record?: number };
  sourceRowCount: number | null;
}
interface CommitResponse {
  committed: number;
  inserted: number;
  updated: number;
  skipped: number;
  tableName: string;
  mode: string;
  customFieldValues: number;
}
interface BatchStatus {
  id: string;
  status: string;
  totalRecords: number;
  rows: { total: number; pending: number; errors: number; matched: number };
  matchCounts: { matched: number; ambiguous: number; new_record: number };
}

/**
 * Saved import-template PROFILE (item 16.4) — a named column_mapping_json v1 map
 * (e.g. QBO-online vs QBO-desktop) + the exact source-header set it was captured
 * from. Server: src/routes (generic CRUD /api/import-templates, table
 * import_templates). The picker exact-matches a parsed file's header set against
 * source_headers_json to auto-apply a profile's mapping.
 */
interface ImportTemplate {
  id: string;
  organization_id: string;
  name: string;
  source_type: string | null;
  target_entity_type: string;
  description: string | null;
  column_mapping_json: string;
  source_headers_json: string | null;
  created_at: string;
}

/** The frozen v1 mapping shape as stored in a profile (subset this wizard emits). */
interface StoredColumnMapping {
  target_entity_type: string;
  mappings: Array<{
    source_column: string;
    target_kind?: 'column' | 'custom_field' | 'ignore';
    custom_field?: { label?: string; field_key?: string; data_type?: string };
  }>;
}

function safeParse<T>(json: string | null | undefined): T | null {
  if (!json) return null;
  try { return JSON.parse(json) as T; } catch { return null; }
}

/** Order-independent exact set-match of two header lists (trimmed). */
function headersExactMatch(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b.map(h => h.trim()));
  return a.every(h => setB.has(h.trim()));
}

const DATA_TYPES: ReadonlyArray<ParsedColumn['detected_type']> = ['text', 'number', 'date', 'boolean'];

function humanizeToLabel(name: string): string {
  return name.trim();
}

/** label → custom_field.field_key per the v1 contract (snake_case key). */
function fieldKeyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'field';
}

const STEPS = [
  { label: 'Source', description: 'Where the data comes from' },
  { label: 'Map', description: 'Columns to fields' },
  { label: 'Review', description: 'Check the rows' },
  { label: 'Commit', description: 'Write the data' },
];

/**
 * Where the rows come from. Both sources converge on the SAME review step and the
 * SAME commit — reading a document is an additional option, not a replacement, so
 * `spreadsheet` stays the default and its path is untouched.
 */
type ImportSource = 'spreadsheet' | 'document';

/** Availability of AI document reading — GET /api/document-extract/availability. */
interface ExtractAvailability {
  available: boolean;
  reason?: string;
  documentTypes?: string[];
}

/** A document that can be read — GET /api/documents/search. */
interface DocumentRow {
  id: string;
  name: string;
  filename: string;
  document_type: string | null;
  year: number | null;
}

/** A staged row read back for review — GET /api/staged-records?import_batch_id=… */
interface StagedRecordRow {
  id: string;
  row_number: number;
  entity_type: string;
  match_status: string;
  review_status: string;
  raw_data_json: string;
  validation_errors_json: string | null;
}

/** POST /api/document-extract/document/:id (inline) — services/document-extract. */
interface ExtractResult {
  queued?: boolean;
  status?: string;
  error?: string | null;
  rows_staged?: number;
  extracted_type?: string | null;
  classified_type?: string | null;
  staged?: { batchId: string; table: string; totalRecords: number; validCount: number; errorCount: number };
}

export function ImportWizard() {
  const { toast } = useToast();
  const [fileName, setFileName] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [columns, setColumns] = useState<ParsedColumn[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [targetClientId, setTargetClientId] = useState('');
  const [targetEntity, setTargetEntity] = useState('parcels');
  const [mappings, setMappings] = useState<Record<string, ColumnMapping>>({});
  const [batch, setBatch] = useState<StageResponse | null>(null);
  const [committing, setCommitting] = useState(false);
  const [summary, setSummary] = useState<CommitResponse | null>(null);
  // Saved import-template PROFILE currently applied (item 16.4). Passed to /stage
  // as template_id so the batch records which profile produced it.
  const [profileId, setProfileId] = useState('');
  const [profileName, setProfileName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  // Which source the rows come from. Spreadsheet is the default + the untouched path.
  const [source, setSource] = useState<ImportSource>('spreadsheet');
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [reading, setReading] = useState(false);
  const [readResult, setReadResult] = useState<ExtractResult | null>(null);

  const canImport = useEntityPermission('import_batch', 'can_add');
  const queryClient = useQueryClient();

  // Saved profiles for the CURRENT record type — the picker + exact-match source.
  const profilesQuery = useQuery<ImportTemplate[]>({
    queryKey: ['import-templates', targetEntity],
    queryFn: async ({ signal }) => {
      const resp = await api.get<ImportTemplate[] | { rows: ImportTemplate[] }>(
        `/api/import-templates?target_entity_type=${encodeURIComponent(targetEntity)}`, signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: canImport,
  });

  const userQuery = useQuery<{ role: string }>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<{ role: string }>('/api/current-user', signal),
    retry: (n, e) => (e instanceof ApiAuthError ? false : n < 2),
  });

  const clientsQuery = useQuery<ClientRow[]>({
    queryKey: ['clients-for-import'],
    // crud.js returns a { rows, total, page, limit } envelope when ?limit= is
    // present and clamps limit to <=200 — ask for the cap and unwrap defensively.
    queryFn: async ({ signal }) => {
      const resp = await api.get<ClientRow[] | { rows: ClientRow[] }>('/api/clients?limit=200', signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: canImport,
  });

  // Is AI document reading offerable? When it isn't, the source is rendered
  // visible-but-DISABLED with the server's plain-language reason and the spreadsheet
  // source stays selected — an unavailable option must explain itself, never dead-end.
  const extractAvailabilityQuery = useQuery<ExtractAvailability>({
    queryKey: ['document-extract-availability'],
    queryFn: ({ signal }) => api.get<ExtractAvailability>('/api/document-extract/availability', signal),
    enabled: canImport,
    // A 403 (no import grant) or any failure means "not offerable", not a page error.
    retry: false,
  });

  // Documents that can be read. Only fetched once the AI source is actually chosen, so
  // the default spreadsheet path issues no extra request.
  const readableDocumentsQuery = useQuery<DocumentRow[]>({
    queryKey: ['documents-for-extraction'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<DocumentRow[] | { rows: DocumentRow[] }>('/api/documents/search?limit=100', signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: canImport && source === 'document',
  });

  // Hooks must run on EVERY render (before any early return) or React throws a
  // hooks-order violation → the top-level error boundary white-screens the SPA.
  // detectedHeaders/detectedProfile depend only on the `columns` state + the
  // profiles query above, so hoist them above the isLoading / access-denied guards
  // (a cold load of /imports hits `userQuery.isLoading` first, then falls through
  // once current-user resolves — the classic more-hooks-than-last-render trap).
  const detectedHeaders = useMemo(() => columns.map(c => c.name), [columns]);
  // Exact-match auto-detection: a saved profile whose source-header SET is
  // identical to this file's parsed headers (order-independent). null when none.
  const detectedProfile = useMemo(() => {
    if (detectedHeaders.length === 0) return null;
    for (const p of profilesQuery.data ?? []) {
      const saved = safeParse<string[]>(p.source_headers_json);
      if (saved && headersExactMatch(detectedHeaders, saved)) return p;
    }
    return null;
  }, [detectedHeaders, profilesQuery.data]);

  if (userQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;

  if (!canImport) {
    return (
      <div style={{
        padding: 'var(--sp-5)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>Importing is restricted to users with import permission.</p>
      </div>
    );
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setColumns([]);
    setMappings({});
    setSourceText('');
    setBatch(null);
    setProfileId('');
    setProfileName('');

    // Binary .xlsx is not supported by the shipped parser (text backend only).
    if (/\.xlsx?$/i.test(f.name)) {
      toast({ variant: 'error', message: 'Binary Excel (.xlsx) is not yet supported — export the sheet as CSV or TSV first.' });
      return;
    }
    const text = await f.text();
    if (text.includes('\u0000') || text.startsWith('PK')) {
      toast({ variant: 'error', message: 'This looks like a binary file. Export the sheet as CSV or TSV first.' });
      return;
    }

    setDetecting(true);
    try {
      // Column-detection probe: the parser profiles ALL columns regardless of
      // the mapping, so a single 'ignore' entry satisfies the frozen v1
      // contract (mappings must be non-empty) without mapping anything yet.
      const probe = await api.post<PreviewResponse>('/api/imports/preview', {
        source: { text },
        column_mapping: {
          version: 'v1',
          target_entity_type: 'parcels',
          options: { trim: true, skip_empty_rows: true },
          mappings: [{ source_column: '__detect__', target_kind: 'ignore' }],
        },
      });
      const cols: ParsedColumn[] = probe.columns.map(pc => ({
        name: pc.source_column,
        sample: pc.sample_values.length > 0 ? String(pc.sample_values[0]) : '',
        detected_type: (DATA_TYPES as readonly string[]).includes(pc.inferred_type)
          ? pc.inferred_type as ParsedColumn['detected_type']
          : 'text',
      }));
      setSourceText(text);
      setColumns(cols);
      setRowCount(probe.rowCount);
      setMappings(Object.fromEntries(cols.map(c => [c.name, {
        target: 'new_custom_field' as MappingTarget,
        label: humanizeToLabel(c.name),
        dataType: c.detected_type,
      }])));
    } catch (err) {
      toast({ variant: 'error', message: err instanceof Error ? err.message : 'Could not parse the file.' });
    } finally {
      setDetecting(false);
    }
  }

  function setMapping(col: string, patch: Partial<ColumnMapping>) {
    // A manual edit diverges from the applied profile — drop the linkage so we
    // don't mislabel the batch as "from" a profile it no longer matches.
    setProfileId('');
    setMappings(prev => ({ ...prev, [col]: { ...prev[col], ...patch } }));
  }

  const mappedCount = Object.values(mappings).filter(m => m.target !== 'skip').length;

  /** Apply a saved profile's column_mapping onto the wizard's per-column state. */
  function applyProfile(profile: ImportTemplate) {
    const parsed = safeParse<StoredColumnMapping>(profile.column_mapping_json);
    if (!parsed || !Array.isArray(parsed.mappings)) {
      toast({ variant: 'error', message: 'This profile is unreadable and cannot be applied.' });
      return;
    }
    const byCol = new Map(parsed.mappings.map(m => [m.source_column, m]));
    setMappings(prev => {
      const next: Record<string, ColumnMapping> = { ...prev };
      for (const c of columns) {
        const m = byCol.get(c.name);
        if (m && (m.target_kind || 'column') === 'custom_field') {
          const cf = m.custom_field || {};
          const dt = (DATA_TYPES as readonly string[]).includes(cf.data_type || '')
            ? (cf.data_type as ParsedColumn['detected_type']) : c.detected_type;
          next[c.name] = { target: 'new_custom_field', label: cf.label || c.name, dataType: dt };
        } else {
          // ignore / column-kind / not in the profile → skip in this wizard.
          next[c.name] = { target: 'skip', label: humanizeToLabel(c.name), dataType: c.detected_type };
        }
      }
      return next;
    });
    setProfileId(profile.id);
    setProfileName(profile.name);
    toast({ variant: 'success', message: `Applied profile "${profile.name}".` });
  }

  async function saveProfile() {
    const name = profileName.trim();
    if (!name) { toast({ variant: 'error', message: 'Name the profile before saving.' }); return; }
    setSavingProfile(true);
    try {
      const created = await api.post<ImportTemplate>('/api/import-templates', {
        name,
        source_type: 'csv_upload',
        target_entity_type: targetEntity,
        column_mapping_json: JSON.stringify(buildColumnMapping()),
        source_headers_json: JSON.stringify(detectedHeaders),
      });
      setProfileId(created.id);
      await queryClient.invalidateQueries({ queryKey: ['import-templates'] });
      toast({ variant: 'success', message: `Saved profile "${name}".` });
    } catch (err) {
      toast({ variant: 'error', message: err instanceof Error ? err.message : 'Could not save the profile.' });
    } finally {
      setSavingProfile(false);
    }
  }

  /** Build the frozen column_mapping_json v1 body from the wizard state. */
  function buildColumnMapping() {
    return {
      version: 'v1',
      target_entity_type: targetEntity,
      options: { trim: true, skip_empty_rows: true },
      mappings: columns.map(c => {
        const m = mappings[c.name];
        if (!m || m.target === 'skip') {
          return { source_column: c.name, target_kind: 'ignore' as const };
        }
        return {
          source_column: c.name,
          target_kind: 'custom_field' as const,
          custom_field: {
            field_key: fieldKeyFromLabel(m.label || c.name),
            label: m.label || c.name,
            data_type: m.dataType,
            // Definitions attach at the row's resolved scope on commit:
            // client-targeted imports scope to the client, parcel/property
            // imports scope to the property.
            scope: targetEntity === 'clients' ? 'client' : 'property',
            create_if_missing: true,
          },
        };
      }),
    };
  }

  /**
   * Read the selected document into a staged batch. The rows land in the SAME
   * `import_batches`/`staged_records` shape a spreadsheet's do, so the Review and Commit
   * steps that follow are the existing ones — nothing about them is AI-specific.
   *
   * The column-mapping step is skipped: the mapping came from the document type's
   * extraction schema, not from a header row the user has to align.
   */
  async function readDocument(setStep: (n: number) => void) {
    if (!selectedDocumentId) return;
    setReading(true);
    setReadResult(null);
    setBatch(null);
    try {
      const res = await api.post<ExtractResult>(
        `/api/document-extract/document/${selectedDocumentId}`, {},
      );
      setReadResult(res);

      if (res.staged && res.staged.batchId) {
        setBatch({
          batchId: res.staged.batchId,
          table: res.staged.table,
          totalRecords: res.staged.totalRecords,
          validCount: res.staged.validCount,
          errorCount: res.staged.errorCount,
        } as StageResponse);
        setTargetEntity(res.staged.table);
        toast({
          variant: 'success',
          message: `Read ${res.staged.totalRecords} row${res.staged.totalRecords === 1 ? '' : 's'} from this document — check them before committing.`,
        });
        setStep(2);   // straight to Review; there are no columns to map
        return;
      }
      // Read fine but nothing to stage (unrecognized type, no matching account, an
      // ambiguous account number). The reason is the useful part, so surface it and
      // leave the user on this step to pick a different document or source. For an
      // unrecognized document the useful part is WHICH types the reader knows.
      const message = res.status === 'unrecognized'
        ? unrecognizedMessage(extractAvailabilityQuery.data?.documentTypes)
        : (res.error || 'Nothing could be staged from this document.');
      toast({ variant: 'warning', message });
    } catch (err) {
      toast({ variant: 'error', message: err instanceof Error ? err.message : 'Could not read this document.' });
    } finally {
      setReading(false);
    }
  }

  function canAdvance(step: number): boolean {
    if (step === 0) {
      // A document source advances once its rows are staged (the read does that and
      // jumps straight to Review); the spreadsheet source is unchanged.
      if (source === 'document') return !!batch;
      return columns.length > 0 && !!sourceText && !detecting;
    }
    if (step === 1) return source === 'document' ? !!batch : mappedCount > 0;
    if (step === 2) return !!batch;
    return true;
  }

  async function commit() {
    if (!batch) return;
    setCommitting(true);
    try {
      const res = await api.post<CommitResponse>(`/api/imports/${batch.batchId}/commit`, {});
      setSummary(res);
      // The commit writes real rows into the target table — refresh that entity's
      // active list (['parcels-list'] / ['clients-list'] / …) so the imported rows
      // appear when the operator returns to it within the 60s staleTime.
      queryClient.invalidateQueries({ queryKey: [`${targetEntity}-list`] });
      toast({ variant: 'success', message: `Import committed — ${res.committed} row${res.committed === 1 ? '' : 's'} written.` });
    } catch (err) {
      toast({ variant: 'error', message: err instanceof Error ? err.message : 'Commit failed.' });
    } finally {
      setCommitting(false);
    }
  }

  function resetAll() {
    setSummary(null);
    setSelectedDocumentId('');
    setReadResult(null);
    setFileName('');
    setSourceText('');
    setColumns([]);
    setRowCount(0);
    setMappings({});
    setTargetClientId('');
    setBatch(null);
  }

  if (summary) {
    return (
      <>
        <Header />
        <div style={{
          padding: 'var(--sp-6)', background: 'var(--color-status-green-bg)',
          border: '1px solid var(--color-status-green)', borderRadius: 'var(--r-lg)', marginBottom: 'var(--sp-5)',
        }}>
          <h2 style={{ margin: '0 0 var(--sp-2)', color: 'var(--color-ink)' }}>Import complete</h2>
          <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>
            {summary.committed.toLocaleString()} row{summary.committed === 1 ? '' : 's'} committed to <strong>{summary.tableName}</strong>
            {' '}({summary.inserted.toLocaleString()} inserted, {summary.updated.toLocaleString()} updated, {summary.skipped.toLocaleString()} skipped)
            {' '}· {summary.customFieldValues.toLocaleString()} custom-field value{summary.customFieldValues === 1 ? '' : 's'} written.
          </p>
        </div>
        <Button variant="secondary" onClick={resetAll}>
          Start another import
        </Button>
      </>
    );
  }

  return (
    <>
      <Header />
      <Wizard
        steps={STEPS}
        submitLabel={committing ? 'Committing…' : 'Commit import'}
        submitting={committing}
        canAdvance={canAdvance}
        onSubmit={commit}
      >
        {({ currentStep, setStep }) => (
          <div style={{ marginTop: 'var(--sp-5)' }}>
            {currentStep === 0 && (
              <Card>
                <SourcePicker
                  source={source}
                  onChange={next => { setSource(next); setReadResult(null); }}
                  availability={extractAvailabilityQuery.data}
                  availabilityLoading={extractAvailabilityQuery.isLoading}
                />

                {source === 'spreadsheet' && (
                  <>
                    <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)' }}>
                      Choose a .csv or .tsv file. Binary Excel (.xlsx) is not yet supported — export the sheet as CSV first.
                    </p>
                    <input type="file" accept=".csv,.tsv,.txt,.xlsx" onChange={onPickFile} style={{ fontFamily: 'inherit' }} />
                    {detecting && (
                      <p style={{ margin: 'var(--sp-3) 0 0', color: 'var(--color-muted)' }}>Detecting columns…</p>
                    )}
                    {fileName && columns.length > 0 && (
                      <p style={{ margin: 'var(--sp-3) 0 0' }}>
                        <Badge variant="success" dot>{fileName}</Badge>
                        <span style={{ color: 'var(--color-muted)', marginLeft: 'var(--sp-2)' }}>
                          {columns.length} column{columns.length === 1 ? '' : 's'} · {rowCount.toLocaleString()} row{rowCount === 1 ? '' : 's'} detected
                        </span>
                      </p>
                    )}
                  </>
                )}

                {source === 'document' && (
                  <DocumentSourceStep
                    documents={readableDocumentsQuery.data ?? []}
                    loading={readableDocumentsQuery.isLoading}
                    documentTypes={extractAvailabilityQuery.data?.documentTypes ?? []}
                    selectedDocumentId={selectedDocumentId}
                    onSelect={id => { setSelectedDocumentId(id); setReadResult(null); }}
                    reading={reading}
                    result={readResult}
                    onRead={() => readDocument(setStep)}
                  />
                )}
              </Card>
            )}

            {currentStep === 1 && source === 'document' && (
              <Card>
                <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>
                  There is nothing to map — the fields came from the document type itself, not from a
                  header row. Continue to review what was read.
                </p>
              </Card>
            )}

            {currentStep === 1 && source === 'spreadsheet' && (
              <Card>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
                  <Field label="Client (reference)">
                    <Select
                      options={[{ value: '', label: 'Select a client…' }, ...(clientsQuery.data ?? []).map(c => ({ value: c.id, label: c.name }))]}
                      value={targetClientId}
                      onChange={e => setTargetClientId(e.target.value)}
                    />
                  </Field>
                  <Field label="Record type">
                    <Select
                      options={[{ value: 'parcels', label: 'Parcels' }, { value: 'properties', label: 'Properties' }, { value: 'clients', label: 'Clients' }]}
                      value={targetEntity}
                      onChange={e => setTargetEntity(e.target.value)}
                    />
                  </Field>
                </div>
                <ProfileBar
                  profiles={profilesQuery.data ?? []}
                  loading={profilesQuery.isLoading}
                  profileId={profileId}
                  profileName={profileName}
                  detectedProfile={detectedProfile}
                  savingProfile={savingProfile}
                  canSave={mappedCount > 0}
                  onApply={applyProfile}
                  onProfileNameChange={setProfileName}
                  onSave={saveProfile}
                />
                <MappingTable columns={columns} mappings={mappings} onChange={setMapping} />
              </Card>
            )}

            {currentStep === 2 && (
              <ReviewStep
                sourceText={sourceText}
                columnMapping={buildColumnMapping()}
                templateId={profileId || undefined}
                columns={columns}
                mappings={mappings}
                batch={batch}
                preStaged={source === 'document'}
                onStaged={setBatch}
                onDiscarded={() => { setBatch(null); setStep(source === 'document' ? 0 : 1); }}
              />
            )}

            {currentStep === 3 && batch && (
              <Card>
                <StatCardRowSafe>
                  <StatCard label="Rows to commit" value={batch.validCount.toLocaleString()} caption={`of ${batch.totalRecords.toLocaleString()} staged`} accent="hunter" />
                  <StatCard label="Validation errors" value={batch.errorCount.toLocaleString()} caption="skipped at commit" accent={batch.errorCount > 0 ? 'amber' : 'info'} />
                  {source === 'document'
                    ? <StatCard label="Read as" value={readResult?.extracted_type || '—'} caption="document type" accent="info" />
                    : <StatCard label="New custom fields" value={String(mappedCount)} caption="auto-created if missing" accent="info" />}
                  <StatCard label="Target" value={batch.table} caption="record type" accent="amber" />
                </StatCardRowSafe>
                <p style={{ margin: 'var(--sp-4) 0 0', color: 'var(--color-ink-3)' }}>
                  Commit writes the staged rows into <strong>{batch.table}</strong> and materializes the mapped
                  columns as custom fields and values. Rows with validation errors are skipped.
                </p>
              </Card>
            )}
          </div>
        )}
      </Wizard>
    </>
  );
}

/**
 * SourcePicker — where the rows come from.
 *
 * Spreadsheet is FIRST and pre-selected: it is the established path, and the one that
 * always works. Reading a document with AI sits beside it as an option. When that option
 * isn't available (the org has it switched off, or the system isn't configured for it) the
 * card stays VISIBLE but disabled and carries the server's own reason — an option that
 * silently disappears teaches nobody, and one that fails on click wastes a click.
 */
function SourcePicker({
  source, onChange, availability, availabilityLoading,
}: {
  source: ImportSource;
  onChange: (s: ImportSource) => void;
  availability?: ExtractAvailability;
  availabilityLoading: boolean;
}) {
  const aiAvailable = !!availability?.available;
  const aiReason = availability?.reason
    || (availabilityLoading ? 'Checking availability…' : 'AI document reading is not available.');

  const OPTIONS: Array<{
    value: ImportSource; title: string; body: string; disabled: boolean; note?: string;
  }> = [
    {
      value: 'spreadsheet',
      title: 'Spreadsheet or CSV',
      body: 'Upload a .csv or .tsv export and map its columns to fields.',
      disabled: false,
    },
    {
      value: 'document',
      title: 'Read a document',
      body: 'Let AI read an uploaded notice or tax statement and propose the figures for review.',
      disabled: !aiAvailable,
      note: aiAvailable ? undefined : aiReason,
    },
  ];

  return (
    <div style={{ marginBottom: 'var(--sp-4)' }}>
      <span style={{
        display: 'block', marginBottom: 'var(--sp-2)',
        fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
      }}>Source</span>

      <div role="radiogroup" aria-label="Import source" style={{
        display: 'grid', gap: 'var(--sp-3)',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
      }}>
        {OPTIONS.map(opt => {
          const selected = source === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={opt.disabled}
              onClick={() => !opt.disabled && onChange(opt.value)}
              style={{
                textAlign: 'left', font: 'inherit', cursor: opt.disabled ? 'not-allowed' : 'pointer',
                padding: 'var(--sp-3)', borderRadius: 'var(--r-md)',
                border: `1px solid ${selected ? 'var(--brand-primary)' : 'var(--color-border)'}`,
                background: selected ? 'var(--brand-primary-bg)' : 'var(--color-card)',
                opacity: opt.disabled ? 0.6 : 1,
              }}
            >
              <span style={{
                display: 'block', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)',
                marginBottom: 'var(--sp-1)',
              }}>{opt.title}</span>
              <span style={{ display: 'block', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                {opt.body}
              </span>
              {opt.note && (
                <span style={{
                  display: 'block', marginTop: 'var(--sp-2)',
                  fontSize: 'var(--fs-xs)', color: 'var(--color-muted)',
                }}>{opt.note}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * DocumentSourceStep — pick an uploaded document and read it.
 *
 * The read produces a staged batch and jumps to Review, so this step is only the choice
 * and the outcome. An outcome that staged nothing (an unrecognized document, an account
 * not on file, an ambiguous account number) is shown here with its reason rather than
 * treated as an error — those are answers, and the reason tells the user what to do next.
 */
function DocumentSourceStep({
  documents, loading, documentTypes, selectedDocumentId, onSelect, reading, result, onRead,
}: {
  documents: DocumentRow[];
  loading: boolean;
  documentTypes: string[];
  selectedDocumentId: string;
  onSelect: (id: string) => void;
  reading: boolean;
  result: ExtractResult | null;
  onRead: () => void;
}) {
  // Documents whose type is one we can read come first — the rest stay selectable, since
  // a misfiled document is exactly the case where the classifier earns its keep.
  const readable = documents.filter(d => d.document_type && documentTypes.includes(d.document_type));
  const others = documents.filter(d => !readable.includes(d));
  const labelFor = (d: DocumentRow) =>
    `${d.name || d.filename}${d.document_type ? ` — ${humanizeToken(d.document_type)}` : ''}${d.year ? ` (${d.year})` : ''}`;

  return (
    <>
      <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)' }}>
        Pick a document already uploaded to the platform. Its figures are proposed for review —
        nothing is written until you commit.
        {readsSentence(documentTypes) ? <> {readsSentence(documentTypes)}</> : null}
      </p>

      <Field label="Document">
        <Select
          options={[
            { value: '', label: loading ? 'Loading documents…' : 'Select a document…' },
            ...readable.map(d => ({ value: d.id, label: labelFor(d) })),
            ...others.map(d => ({ value: d.id, label: labelFor(d) })),
          ]}
          value={selectedDocumentId}
          onChange={e => onSelect(e.target.value)}
        />
      </Field>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Button onClick={onRead} disabled={!selectedDocumentId || reading}>
          {reading ? 'Reading…' : 'Read this document'}
        </Button>
      </div>

      {result && !result.staged && (
        <div style={{
          marginTop: 'var(--sp-4)', padding: 'var(--sp-3)', borderRadius: 'var(--r-md)',
          background: 'var(--color-status-amber-bg)', border: '1px solid var(--color-status-amber)',
        }}>
          <p style={{ margin: 0, color: 'var(--color-ink)' }}>
            <strong>Nothing staged.</strong>{' '}
            {result.status === 'unrecognized'
              ? unrecognizedMessage(documentTypes)
              : (result.error || 'This document produced no rows.')}
          </p>
          {result.classified_type && (
            <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              Read as: {result.classified_type}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/**
 * ReviewStep — stages the parsed + mapped source on entry and shows the staged
 * batch (counts, live status, and a mapped sample). Re-entering the step rolls
 * back the previous batch and stages fresh so the review always reflects the
 * CURRENT mapping. Discard rolls back via DELETE /api/imports/:batchId.
 */
function ReviewStep({
  sourceText, columnMapping, templateId, columns, mappings, batch, onStaged, onDiscarded,
  preStaged = false,
}: {
  sourceText: string;
  columnMapping: Record<string, unknown>;
  templateId?: string;
  columns: ParsedColumn[];
  mappings: Record<string, ColumnMapping>;
  batch: StageResponse | null;
  onStaged: (b: StageResponse | null) => void;
  onDiscarded: () => void;
  /**
   * True when the batch arrived ALREADY staged — the document-reading source stages as
   * part of the read, so this step must show that batch rather than re-staging (there is
   * no source text or column mapping to stage from). Everything below this point is the
   * same review + discard the spreadsheet path gets.
   */
  preStaged?: boolean;
}) {
  const { toast } = useToast();
  const [stageError, setStageError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<MappedPreviewRow[]>([]);
  const [discarding, setDiscarding] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (preStaged) return;          // already staged by the document read
    if (ranRef.current) return;
    ranRef.current = true;
    let cancelled = false;
    (async () => {
      // Roll back any batch staged on a previous visit — the mapping may have
      // changed, and a fresh stage keeps the review truthful.
      if (batch) {
        try { await api.delete(`/api/imports/${batch.batchId}`); } catch { /* already gone */ }
        if (!cancelled) onStaged(null);
      }
      try {
        const [preview, staged] = await Promise.all([
          api.post<PreviewResponse>('/api/imports/preview', { source: { text: sourceText }, column_mapping: columnMapping }),
          // template_id (item 16.4): when a saved profile produced this mapping,
          // link the staged batch to it (import_batches.template_id).
          api.post<StageResponse>('/api/imports/stage', { source: { text: sourceText }, column_mapping: columnMapping, template_id: templateId }),
        ]);
        if (cancelled) return;
        setPreviewRows(preview.rows.slice(0, 4));
        onStaged(staged);
      } catch (err) {
        if (!cancelled) setStageError(err instanceof Error ? err.message : 'Staging failed.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusQuery = useQuery<BatchStatus>({
    queryKey: ['import-batch-status', batch?.batchId],
    queryFn: ({ signal }) => api.get<BatchStatus>(`/api/imports/${batch!.batchId}`, signal),
    enabled: !!batch,
  });

  async function discard() {
    if (!batch) return;
    setDiscarding(true);
    try {
      await api.delete(`/api/imports/${batch.batchId}`);
      toast({ variant: 'info', message: 'Staged batch discarded.' });
      onStaged(null);
      onDiscarded();
    } catch (err) {
      toast({ variant: 'error', message: err instanceof Error ? err.message : 'Rollback failed.' });
    } finally {
      setDiscarding(false);
    }
  }

  if (stageError) {
    return (
      <Card>
        <p style={{ margin: 0, color: 'var(--color-status-red)' }}>Staging failed: {stageError}</p>
      </Card>
    );
  }

  if (!batch) {
    return (
      <Card>
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>Staging rows for review…</p>
      </Card>
    );
  }

  const match = batch.matchCounts ?? {};
  const status = statusQuery.data;

  return (
    <Card>
      <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)' }}>
        {batch.totalRecords.toLocaleString()} row{batch.totalRecords === 1 ? '' : 's'} staged
        {' '}· {batch.validCount.toLocaleString()} valid · {batch.errorCount.toLocaleString()} error{batch.errorCount === 1 ? '' : 's'}
        {' '}· {(match.new_record ?? 0).toLocaleString()} new · {(match.matched ?? 0).toLocaleString()} matched · {(match.ambiguous ?? 0).toLocaleString()} ambiguous
        {status && <> · batch status <Badge variant={status.status === 'pending' || status.status === 'staged' ? 'info' : 'success'}>{humanizeToken(status.status || '')}</Badge></>}
      </p>
      {preStaged
        ? <ExtractedRowsTable batchId={batch.batchId} />
        : <ReviewTable columns={columns} mappings={mappings} previewRows={previewRows} />}
      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Button variant="ghost" size="sm" onClick={discard} disabled={discarding}>
          {discarding ? 'Discarding…' : 'Discard staged batch'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * ExtractedRowsTable — what a document read actually produced, field by field.
 *
 * The spreadsheet path's review table is built from the column MAPPING, which a document
 * read has none of (its fields came from the document type's schema), so that table renders
 * "No columns mapped" — precisely useless at the moment a reviewer is deciding whether to
 * commit. This shows the staged values instead, with the per-field confidence the reader
 * reported, lowest first, so an uncertain figure is the first thing the eye lands on.
 *
 * Reads the rows back through the existing staged-records CRUD surface — no new endpoint.
 */
function ExtractedRowsTable({ batchId }: { batchId: string }) {
  const { data, isLoading, error } = useQuery<StagedRecordRow[]>({
    queryKey: ['staged-records', batchId],
    queryFn: async ({ signal }) => {
      const resp = await api.get<StagedRecordRow[] | { rows: StagedRecordRow[] }>(
        `/api/staged-records?import_batch_id=${encodeURIComponent(batchId)}`, signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
  });

  if (isLoading) return <p style={{ margin: 0, color: 'var(--color-muted)' }}>Loading what was read…</p>;
  if (error || !data) {
    return <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Couldn’t load the extracted rows.</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      {data.map(record => {
        const row = safeParse<Record<string, unknown>>(record.raw_data_json) || {};
        const confidence = (row.__confidence || {}) as Record<string, number>;
        const notes = typeof row.__notes === 'string' ? row.__notes : null;
        const problems = safeParse<string[]>(record.validation_errors_json) || [];

        // Real columns only — the reserved `__` keys are pipeline meta, not data.
        const fields = Object.entries(row)
          .filter(([k, val]) => !k.startsWith('__') && val !== null && val !== undefined && val !== '')
          .map(([k, val]) => ({
            field: k,
            value: String(val),
            // Confidence is keyed by the SCHEMA field name, which usually matches the
            // column; when it doesn't there simply is no score to show.
            confidence: typeof confidence[k] === 'number' ? confidence[k] : null,
          }))
          .sort((a, b) => (a.confidence ?? 2) - (b.confidence ?? 2));

        return (
          <div key={record.id} style={{
            border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap',
              padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-card)',
              borderBottom: '1px solid var(--color-border)',
            }}>
              <strong style={{ fontSize: 'var(--fs-sm)' }}>Row {record.row_number}</strong>
              <Badge variant={record.review_status === 'error' ? 'warning' : 'success'} dot>
                {record.review_status === 'error' ? 'needs a look' : 'ready'}
              </Badge>
              {record.match_status === 'matched' && <Badge variant="info">updates an existing record</Badge>}
              {record.match_status === 'new_record' && <Badge variant="neutral">new record</Badge>}
            </div>

            {problems.length > 0 && (
              <div style={{
                padding: 'var(--sp-3)', background: 'var(--color-status-amber-bg)',
                borderBottom: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)',
              }}>
                {problems.map((p, i) => <p key={i} style={{ margin: i ? 'var(--sp-1) 0 0' : 0 }}>{p}</p>)}
              </div>
            )}
            {notes && (
              <div style={{
                padding: 'var(--sp-3)', borderBottom: '1px solid var(--color-border)',
                fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)',
              }}>{notes}</div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
              <tbody>
                {fields.map(f => (
                  <tr key={f.field} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--sp-2) var(--sp-3)', color: 'var(--color-ink-3)', width: '38%' }}>
                      {humanizeToken(f.field)}
                    </td>
                    <td style={{ padding: 'var(--sp-2) var(--sp-3)', fontFamily: 'var(--font-mono)' }}>{f.value}</td>
                    <td style={{ padding: 'var(--sp-2) var(--sp-3)', width: '22%', textAlign: 'right' }}>
                      {f.confidence === null ? null : (
                        <Badge variant={f.confidence >= 0.85 ? 'success' : f.confidence >= 0.6 ? 'warning' : 'danger'}>
                          {Math.round(f.confidence * 100)}% sure
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

/**
 * ProfileBar — saved import-template PROFILES (item 16.4): a picker to apply a
 * saved column map, an exact-match auto-detect banner, and a save-as-profile
 * control. Wired to the REAL /api/import-templates CRUD surface.
 */
function ProfileBar({
  profiles, loading, profileId, profileName, detectedProfile, savingProfile, canSave,
  onApply, onProfileNameChange, onSave,
}: {
  profiles: ImportTemplate[];
  loading: boolean;
  profileId: string;
  profileName: string;
  detectedProfile: ImportTemplate | null;
  savingProfile: boolean;
  canSave: boolean;
  onApply: (p: ImportTemplate) => void;
  onProfileNameChange: (v: string) => void;
  onSave: () => void;
}) {
  const applied = profiles.find(p => p.id === profileId) || null;
  const detectedButNotApplied = detectedProfile && detectedProfile.id !== profileId;

  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
      padding: 'var(--sp-3)', marginBottom: 'var(--sp-4)', background: 'var(--color-surface-1, var(--color-card))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
        <span style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>Import profile</span>
        {applied && <Badge variant="success" dot>Using “{applied.name}”</Badge>}
      </div>

      {detectedButNotApplied && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-2)',
          padding: 'var(--sp-2) var(--sp-3)', marginBottom: 'var(--sp-3)',
          background: 'var(--color-status-green-bg)', border: '1px solid var(--color-status-green)',
          borderRadius: 'var(--r-md)',
        }}>
          <Badge variant="success" dot>Exact match</Badge>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
            These columns match saved profile <strong>{detectedProfile!.name}</strong>.
          </span>
          <Button size="sm" variant="secondary" onClick={() => onApply(detectedProfile!)}>Apply profile</Button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 220, flex: '1 1 220px' }}>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Apply a saved profile</span>
          <Select
            options={[
              { value: '', label: loading ? 'Loading profiles…' : profiles.length ? 'Select a profile…' : 'No saved profiles yet' },
              ...profiles.map(p => ({ value: p.id, label: p.source_type ? `${p.name} · ${p.source_type}` : p.name })),
            ]}
            value={profileId}
            disabled={loading || profiles.length === 0}
            onChange={e => { const p = profiles.find(x => x.id === e.target.value); if (p) onApply(p); }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 220, flex: '1 1 220px' }}>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Save current mapping as a profile</span>
          <Input
            placeholder="e.g. QBO Online — Properties"
            value={profileName}
            onChange={e => onProfileNameChange(e.target.value)}
          />
        </label>
        <Button
          variant="secondary"
          disabled={savingProfile || !canSave || !profileName.trim()}
          onClick={onSave}
        >
          {savingProfile ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </div>
  );
}

function MappingTable({
  columns, mappings, onChange,
}: {
  columns: ParsedColumn[];
  mappings: Record<string, ColumnMapping>;
  onChange: (col: string, patch: Partial<ColumnMapping>) => void;
}) {
  // On a phone the four-across mapping row won't fit (and a long CSV column name
  // could force horizontal overflow), so each mapping stacks to a single column.
  const isCompact = useIsCompact();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {columns.map(c => {
        const m = mappings[c.name];
        return (
          <div key={c.name} style={{
            display: 'grid',
            gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
            gap: 'var(--sp-3)', alignItems: 'center',
            padding: 'var(--sp-2) var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', overflowWrap: 'anywhere' }}>{c.name}</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>e.g. {c.sample}</div>
            </div>
            <Select
              options={[{ value: 'new_custom_field', label: 'New custom field' }, { value: 'skip', label: 'Skip' }]}
              value={m.target}
              onChange={e => onChange(c.name, { target: e.target.value as MappingTarget })}
            />
            <Input
              value={m.label}
              disabled={m.target === 'skip'}
              onChange={e => onChange(c.name, { label: e.target.value })}
            />
            <Select
              options={[{ value: 'text', label: 'Text' }, { value: 'number', label: 'Number' }, { value: 'date', label: 'Date' }, { value: 'boolean', label: 'Yes / No' }]}
              value={m.dataType}
              disabled={m.target === 'skip'}
              onChange={e => onChange(c.name, { dataType: e.target.value as ParsedColumn['detected_type'] })}
            />
          </div>
        );
      })}
    </div>
  );
}

function ReviewTable({
  columns, mappings, previewRows,
}: {
  columns: ParsedColumn[];
  mappings: Record<string, ColumnMapping>;
  previewRows: MappedPreviewRow[];
}) {
  const active = columns.filter(c => mappings[c.name]?.target !== 'skip');

  const rows = useMemo(() => previewRows.map((pr, i) => {
    const row: Record<string, string> = { __row: String(i + 1), __error: pr.error ?? '' };
    active.forEach(c => {
      const m = mappings[c.name];
      const key = fieldKeyFromLabel(m?.label || c.name);
      const cf = pr.customFields.find(f => f.field_key === key);
      const v = cf?.value ?? pr.record[key];
      row[c.name] = v == null ? '' : String(v);
    });
    return row;
  }), [previewRows, active, mappings]);

  const cols = useMemo<ColumnDef<Record<string, string>>[]>(() => [
    { accessorKey: '__row', header: '#' },
    ...active.map(c => ({
      accessorKey: c.name,
      header: mappings[c.name]?.label || c.name,
      cell: ({ getValue }: { getValue: () => unknown }) => String(getValue() ?? ''),
    })),
    {
      id: 'valid',
      header: 'Validation',
      cell: ({ row }) => row.original.__error
        ? <Badge variant="danger" dot>{row.original.__error}</Badge>
        : <Badge variant="success" dot>Valid</Badge>,
    },
  ], [active, mappings]);

  if (active.length === 0) return <p style={{ color: 'var(--color-muted)' }}>No columns mapped.</p>;
  if (rows.length === 0) return <p style={{ color: 'var(--color-muted)' }}>No sample rows to preview.</p>;
  return <DataTable data={rows} columns={cols} />;
}

function Header() {
  return (
    <header style={{ marginBottom: 'var(--sp-5)' }}>
      <h1 style={{
        margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)',
        letterSpacing: 'var(--ls-tight)', color: 'var(--color-ink)',
      }}>Import data</h1>
      <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
        Bring outside data in from a spreadsheet, or let AI read an uploaded document — both land in
        the same review before anything is written.
      </p>
    </header>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
    }}>{children}</div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 200 }}>
      <span style={{
        fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
      }}>{label}</span>
      {children}
    </label>
  );
}

function StatCardRowSafe({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 'var(--sp-4)' }}>
      {children}
    </div>
  );
}
